import useCallbackRef from 'hooks/useCallbackRef';
import ExclamationIcon from 'icons/Exclamation.icon';
import SettingsIcon from 'icons/Settings.icon';
import { Button } from 'ncoded-component-library';
import React, { useContext } from 'react';
import { DataRoomMember } from 'router/subrouters/DataRoom/models/DataRoomMember';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { SideDrawerRef } from '../../../SideDrawer/SideDrawer.component';
import { DocumentsAvailabilityDrawer } from '../DocumentAvailabilityDrawer';
import { TABLE_COLUMNS } from './data';
import { useTranslation } from 'react-i18next';

type CellProps = {
  member: DataRoomMember;
  updateMember?: (member: DataRoomMember) => void;
};

export const BODY_CONTENT_GENERATE: Record<
  typeof TABLE_COLUMNS[number],
  (
    val: DataRoomMember,
    updateMember: (member: DataRoomMember) => void,
  ) => React.ReactNode
> = {
  member: (member: DataRoomMember) => <MemberCellComponent member={member} />,
  views: (member: DataRoomMember) => <ViewsCellComponent member={member} />,
  files: (member: DataRoomMember) => <FilesCellComponent member={member} />,
  manage: (
    member: DataRoomMember,
    updateMember?: (member: DataRoomMember) => void,
  ) => <ManageCellComponent member={member} updateMember={updateMember} />,
};

export const MemberCellComponent: React.FC<CellProps> = ({ member }) => {
  const { fullName, email, isAccepted } = member;

  const name = fullName || email;

  const { t } = useTranslation();

  return (
    <div className="member-cell">
      <span>{isAccepted ? name : email || t('unknownUser')}</span>
    </div>
  );
};

export const ViewsCellComponent: React.FC<CellProps> = ({ member }) => {
  const { numberOfViewedFiles } = member;

  return (
    <div className="views-cell">
      <span>{numberOfViewedFiles}</span>
    </div>
  );
};

export const FilesCellComponent: React.FC<CellProps> = ({ member }) => {
  const { accessableDataRoomNodesView } = member;

  const numberOfAccessableFiles = accessableDataRoomNodesView?.length;

  return (
    <div className="files-cell">
      {numberOfAccessableFiles}
      {!numberOfAccessableFiles && <ExclamationIcon />}
    </div>
  );
};

export const ManageCellComponent: React.FC<CellProps> = ({
  member,
  updateMember,
}) => {
  const { doesntHaveAccess } = useContext(DataRoomContext);

  const [manageDocuments, manageDocumentsRef] =
    useCallbackRef<SideDrawerRef>(null);

  if (doesntHaveAccess) return null;

  return (
    <>
      <div className="bat-manage-cell">
        <Button
          variant="link"
          icon={SettingsIcon}
          onClick={() => manageDocuments.setOpen(true)}
        />
      </div>
      <DocumentsAvailabilityDrawer
        ref={manageDocumentsRef}
        member={member}
        updateMember={updateMember}
        isBuySide
      />
    </>
  );
};
